// All money fields are integer cents. See spec § Money Representation.

export type DieFace = 1 | 2 | 3 | 4 | 5 | 6;
export type PointNumber = 4 | 5 | 6 | 8 | 9 | 10;

export type BetId = string & { readonly __brand: 'BetId' };

export type BetType =
  | 'pass' | 'dontPass' | 'come' | 'dontCome'
  | 'odds' | 'dontOdds'
  | 'place' | 'buy' | 'lay'
  | 'field'
  | 'hardway'
  | 'bigSix' | 'bigEight'
  | 'any7' | 'anyCraps' | 'yo' | 'aces' | 'boxcars' | 'aceDeuce' | 'cAndE'
  | 'horn' | 'world' | 'hop'
  | 'small' | 'tall' | 'all';

export interface BetOptions {
  number?: PointNumber;
  parentBetId?: BetId;
  hopFaces?: [DieFace, DieFace];
  working?: boolean;
}

export interface ActiveBet {
  id: BetId;
  type: BetType;
  amount: number;            // integer cents
  working: boolean;
  number?: PointNumber;
  parentBetId?: BetId;
  hopFaces?: [DieFace, DieFace];
  metadata?: Record<string, unknown>;
}

export interface RollResult {
  d1: DieFace;
  d2: DieFace;
  total: number;
  isHardway: boolean;
  isCraps: boolean;
  isNatural: boolean;
  isPoint: boolean;
  isSeven: boolean;
}

export type Phase = 'COME_OUT' | 'POINT';

export interface TableState {
  phase: Phase;
  point?: PointNumber;
  bankroll: number;          // integer cents
  bets: ActiveBet[];
  history: RollResult[];
}

/**
 * Persisted-session blob for craps. Captured by Engine.serialize() so the
 * engine can resume after a page reload or table switch with the player's
 * chips still on the layout.
 *
 * `bankroll` is optional for back-compat: the player-side persistence layer
 * (packages/game/src/persistence.ts) stores bankroll separately at the v2
 * root and passes snapshots without it. The session-replay transport
 * (TableEntered.initialState) populates it so a spectator catching up has
 * everything they need in a single hydrate call.
 */
export interface CrapsSnapshot {
  phase: Phase;
  point?: PointNumber;
  bets: ActiveBet[];
  history: RollResult[];
  bankroll?: number;          // integer cents; optional for back-compat
  /** id-generator counter at serialize time, so replays reconstruct exact bet IDs
   *  even from an empty-felt keyframe. Optional: older keyframes fall back to
   *  seeding from the bets present. */
  nextBetSeq?: number;
}

export interface TableRules {
  oddsCap: '1x' | '2x' | '3-4-5x' | '5x' | '10x' | '100x';
  vigPolicy: 'onPlace' | 'onWin';
  field12Payout: 2 | 3;
  fieldDeucePayout: 2 | 3;
  comeOutPlaceWorking: boolean;
  tableMin: number;          // integer cents
  tableMax: number;          // integer cents
  dontPassPushOn: 2 | 12;    // Vegas: 12, Reno: 2
  /**
   * Multipliers for the All / Tall / Small bonus bets. The multiplier is
   * applied to the bet amount when the required-number set completes
   * before any 7. Vegas standard: 30 / 30 / 150. Higher tiers offer better
   * payouts (see game-persistence layer for per-tier overrides).
   */
  atsPayouts: { small: number; tall: number; all: number };
}

export const DEFAULT_RULES: TableRules = {
  oddsCap: '3-4-5x',
  vigPolicy: 'onWin',
  field12Payout: 3,
  fieldDeucePayout: 2,
  comeOutPlaceWorking: false,
  tableMin: 500,
  tableMax: 100_000,
  dontPassPushOn: 12,
  atsPayouts: { small: 30, tall: 30, all: 150 },
};

export type ResolutionOutcome = 'win' | 'lose' | 'push' | 'continue';

export interface Resolution {
  betId: BetId;
  outcome: ResolutionOutcome;
  payout: number;            // integer cents — additional winnings only
  /**
   * Optional bet-metadata mutation applied by `applyResolutions` when
   * outcome is 'continue'. First user is ATS bets, which need to record
   * the seen-numbers set across rolls without being resolved. Ignored for
   * win/lose/push (the bet is either removed or has its stake returned).
   */
  betMetadataPatch?: Record<string, unknown>;
}

export interface PostProcessContext {
  roll: RollResult;
  state: TableState;
}

export type ResolutionPostProcessor =
  (resolutions: Resolution[], ctx: PostProcessContext) => Resolution[];

export type EngineEvent =
  | { type: 'BetPlaced'; bet: ActiveBet }
  | { type: 'BetRemoved'; id: BetId; bet?: ActiveBet }
  | { type: 'BetWorkingChanged'; id: BetId; working: boolean }
  | { type: 'RollStarted'; roll?: RollResult }
  | { type: 'RollResolved'; roll: RollResult; resolutions: Resolution[] }
  | { type: 'PointEstablished'; point: PointNumber }
  | { type: 'PointMade' }
  | { type: 'SevenOut' }
  | { type: 'PhaseChanged'; phase: Phase }
  | { type: 'BankrollChanged'; bankroll: number };

export type ResolveFn = (bet: ActiveBet, roll: RollResult, state: TableState, rules: TableRules) => Resolution;
